import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, PoundSterling, Handshake, Newspaper, Heart, Settings, ExternalLink, Lock, FileCode2, CreditCard, Eye, ShoppingBag, Landmark } from "lucide-react";
import { TopBar } from "@/components/Brand";
import { PageLoader, Empty } from "@/components/kit";
import { api, errMsg, initials } from "@/lib/api";
import Overview from "./Overview";
import Squad from "./Squad";
import Fixtures from "./Fixtures";
import Fees from "./Fees";
import Sponsors from "./Sponsors";
import News from "./News";
import Fans from "./Fans";
import Embeds from "./Embeds";
import Billing from "./Billing";
import TeamSettings from "./TeamSettings";
import Shop from "./Shop";
import StripeAccount from "./StripeAccount";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, C: Overview },
  { key: "squad", label: "Squad", icon: Users, C: Squad },
  { key: "fixtures", label: "Fixtures", icon: CalendarDays, C: Fixtures },
  { key: "fees", label: "Fees", icon: PoundSterling, C: Fees },
  { key: "sponsors", label: "Sponsors", icon: Handshake, C: Sponsors },
  { key: "shop", label: "Club shop", icon: ShoppingBag, C: Shop },
  { key: "news", label: "News & Polls", icon: Newspaper, C: News },
  { key: "forms", label: "Forms & Embeds", icon: FileCode2, C: Embeds },
  { key: "fans", label: "Fans", icon: Heart, C: Fans },
  { key: "settings", label: "Hub & Settings", icon: Settings, C: TeamSettings },
  { key: "stripe", label: "Stripe account", icon: Landmark, C: StripeAccount },
  { key: "billing", label: "Plan & Billing", icon: CreditCard, C: Billing },
];

const Sidebar = ({ team, tab, setTab }) => (
  <aside className="lg:sticky lg:top-24 lg:h-fit lg:w-60 lg:shrink-0">
    <div className="mb-6 hidden items-center gap-3 lg:flex">
      {team.logo_url ? <img src={team.logo_url} alt={team.name} className="h-11 w-11 rounded-md bg-white object-contain p-0.5" /> : <span className="flex h-11 w-11 items-center justify-center rounded-md font-display text-lg font-black" style={{ background: team.primary_color, color: "#0B0F17" }}>{initials(team.name)}</span>}
      <div className="min-w-0"><p className="truncate font-display text-xl font-bold uppercase text-white">{team.name}</p><p className="text-xs text-slate-500">{team.sport}</p></div>
    </div>
    <nav className="no-scrollbar -mx-4 flex gap-1 overflow-x-auto px-4 lg:mx-0 lg:flex-col lg:px-0">
      {TABS.map((t) => (
        <button
          key={t.key}
          onClick={() => setTab(t.key)}
          data-testid={`manage-tab-${t.key}`}
          className={`flex shrink-0 items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#CCFF00] text-[#0B0F17]" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}
        >
          <t.icon className="h-4 w-4" /> {t.label}
        </button>
      ))}
    </nav>
    <Link to={`/t/${team.slug}`} target="_blank" className="mt-6 hidden items-center gap-2 text-sm text-slate-400 hover:text-white lg:flex" data-testid="sidebar-view-hub-link">
      <ExternalLink className="h-4 w-4" /> View public hub
    </Link>
  </aside>
);

export default function ManageTeam() {
  const { teamId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/teams/${teamId}`);
      setData(r.data);
    } catch (e) {
      setError({ status: e.response?.status, msg: errMsg(e) });
    }
  }, [teamId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F17]"><TopBar />
        <main className="mx-auto max-w-3xl px-4 py-16" data-testid="manage-error">
          <Empty icon={Lock} title={error.status === 423 ? "Team paused" : "No access"} text={error.msg}>
            <Link to="/dashboard" className="text-[#CCFF00]">Back to my teams</Link>
          </Empty>
        </main>
      </div>
    );
  }
  if (!data) return <PageLoader />;
  const Active = (TABS.find((t) => t.key === tab) || TABS[0]).C;
  const setTab = (k) => setParams({ tab: k });

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <TopBar><Link to="/dashboard" className="hidden text-sm text-slate-400 hover:text-white sm:inline" data-testid="nav-my-teams-link">My teams</Link></TopBar>
      {data.impersonating && (
        <div className="bg-[#CCFF00] px-4 py-2 text-center text-sm font-semibold text-[#0B0F17]" data-testid="impersonation-banner">
          <Eye className="mr-1 inline h-4 w-4" /> You're managing {data.team.name} as platform admin{data.team.status === "paused" ? " (team is paused)" : ""} · <Link to="/admin" className="underline">Back to platform console</Link>
        </div>
      )}
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-8 sm:px-8 lg:flex-row lg:py-12">
        <Sidebar team={data.team} tab={tab} setTab={setTab} />
        <main className="min-w-0 flex-1" data-testid={`manage-panel-${tab}`}>
          <Active teamId={teamId} team={data.team} overview={data} reloadTeam={reload} setTab={setTab} />
        </main>
      </div>
    </div>
  );
}
